import React, { useState } from 'react';
import { format, addMonths, addDays, startOfMonth, endOfMonth, startOfYear, subMonths } from 'date-fns';

interface DateRangeSelectorProps {
  onDateRangeChange: (startDate: Date, endDate: Date) => void;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ onDateRangeChange }) => {
  const [dateRangeLabel, setDateRangeLabel] = useState('All Time');
  const [showDropdown, setShowDropdown] = useState(false);

  const handlePresetClick = (preset: string) => {
    let start: Date;
    let end: Date = new Date();
    let label: string;

    const today = new Date();

    switch (preset) {
      case 'thisMonth':
        start = startOfMonth(today);
        label = `This Month (${format(start, 'MMMM yyyy')})`;
        break;
      case 'lastMonth':
        start = startOfMonth(addMonths(today, -1));
        end = endOfMonth(addMonths(today, -1));
        label = `Last Month (${format(start, 'MMMM yyyy')})`;
        break;
      case 'thisQuarter':
        const currentMonth = today.getMonth();
        const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
        start = new Date(today.getFullYear(), quarterStartMonth, 1);
        end = new Date(today.getFullYear(), quarterStartMonth + 3, 0);
        label = `This Quarter (Q${Math.floor(currentMonth / 3) + 1} ${today.getFullYear()})`;
        break;
      case 'ytd':
        start = startOfYear(today);
        label = `Year to Date (${today.getFullYear()})`;
        break;
      case 'last3Months':
        start = subMonths(today, 3);
        label = `Last 3 Months`;
        break;
      case 'last6Months':
        start = subMonths(today, 6);
        label = `Last 6 Months`;
        break;
      case 'last30Days':
        start = addDays(today, -30);
        label = 'Last 30 Days';
        break;
      case 'allTime':
        start = new Date(2000, 0, 1);
        label = 'All Time';
        break;
      default:
        start = new Date(2000, 0, 1);
        label = 'All Time';
    }
    
    onDateRangeChange(start, end);
    setDateRangeLabel(label);
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setShowDropdown(!showDropdown)}
        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
        </svg>
        {dateRangeLabel}
      </button>

      {showDropdown && (
        <div className="absolute mt-2 z-10 bg-white rounded-md shadow-lg p-2">
          <div className="grid grid-cols-1 gap-1">
            <button
              onClick={() => handlePresetClick('thisMonth')}
              className="text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-md"
            >
              This Month
            </button>
            <button
              onClick={() => handlePresetClick('lastMonth')}
              className="text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-md"
            >
              Last Month
            </button>
            <button
              onClick={() => handlePresetClick('thisQuarter')}
              className="text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-md"
            >
              This Quarter
            </button>
            <button
              onClick={() => handlePresetClick('ytd')}
              className="text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-md"
            >
              Year to Date
            </button>
            <button
              onClick={() => handlePresetClick('last30Days')}
              className="text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-md"
            >
              Last 30 Days
            </button>
            <button
              onClick={() => handlePresetClick('last3Months')}
              className="text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-md"
            >
              Last 3 Months
            </button>
            <button
              onClick={() => handlePresetClick('last6Months')}
              className="text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-md"
            >
              Last 6 Months
            </button>
            <button
              onClick={() => handlePresetClick('allTime')}
              className="text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-md"
            >
              All Time
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector; 